import React, { ChangeEvent, useEffect, useRef, useState } from 'react';
import styled from 'styled-components';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import {
  BackButton,
  TextButton as Button,
  Checkbox,
  COLORS,
  ComponentM,
  ComponentS,
  ComponentTextStyle,
  ComponentXS,
  DropdownFilter,
  HyperLink,
  IconButton,
  InputLabel,
  PageWidth,
  Size,
  SystemIcons,
  Textarea,
  TextField,
} from '@laerdal/life-react-components';
import { selectMarket, buildMarketPath } from '../../store/slices/globalConfigSlice';
import { useNavigate } from 'react-router-dom';
import { AttachmentDetails, AttachmentHeader, AttachmentsContainer, ChoseFileContainer, StepDescription, StepTitle } from './components';
import { countryList } from '../../model/constants/CountryList';
import { CaseCreateDto } from '../../model/dto/caseCreateDto';
import CaseApi from '../../services/api/CaseApi';
import { CustomTextareaContainera } from './components/steps/StepComponents';
import posthog from 'posthog-js';


const PageContainer = styled.div`
  padding: 24px 12px;
  display: flex;
  flex-direction: column;
  justify-content: center;
  gap: 8px;
`;
const LinkContainer = styled.div`
    margin-left: 46px;
`;

const QcprSupportSuggestionsPage = () => {
  const currentMarket = useSelector(selectMarket);
  const navigate = useNavigate();

  const { t } = useTranslation(['translation', 'qcprSupport']);

  const [email, setEmail] = useState<string>('');
  const [firstName, setFirstName] = useState<string>('');
  const [lastName, setLastName] = useState<string>('');
  const [country, setCountry] = useState<string>('');
  const [description, setDescription] = useState<string>('');
  const [privacyStatement, setPrivacyStatement] = useState<boolean>(false);
  const [creatingCase, setCreatingCase] = useState<boolean>(false);

  const [files, setFiles] = useState<File[]>([]);
  const [fileSizeOverLimit, setFileSizeOverLimit] = useState<boolean>(false);
  const MAX_FILE_SIZE = 21 * 1024 * 1024;

  const handleClick = () => {
    fileInputRef.current?.click();
  };
  const fileInputRef = useRef<HTMLInputElement>(null);
  const handleFileInputChange = (e: ChangeEvent<HTMLInputElement>) => {
    handleFiles(e.target.files);
  };
  const handleFiles = (newFiles: FileList | null) => {
    if (newFiles) {
      setFiles(prevFiles => [...prevFiles, ...Array.from(newFiles)]);
      if (fileInputRef.current) {
        fileInputRef.current.value = ""; // Clear the current file input
      }
      handleFileAttachment();
    }
  }
  const handleButtonClick = (e: React.MouseEvent<HTMLButtonElement>) => {
    e.stopPropagation();
    fileInputRef.current?.click();
  };
  const handleRemoveFile = (index: number) => {
    setFiles(prevFiles => prevFiles.filter((_, i) => i !== index));
  };

  useEffect(() => {
    const totalSize = files.reduce((sum, file) => sum + file.size, 0);
    setFileSizeOverLimit(totalSize > MAX_FILE_SIZE);
  }, [files]);

  useEffect(() => {
    // Track page visit
    posthog.capture('SUPP-QcprSupport-SuggestionsPageVisit', {
      page: 'QcprSupportSuggestions',
      market: currentMarket
    });
  }, [currentMarket]);

  const handleBackClick = () => {
    posthog.capture('SUPP-QcprSupport-SuggestionsBackClicked', {
      page: 'QcprSupportSuggestions',
      market: currentMarket
    });
    navigate(buildMarketPath(currentMarket, '/qcpr-support'));
  };

  const handleFileAttachment = () => {
    posthog.capture('SUPP-QcprSupport-SuggestionsFileAttached', {
      page: 'QcprSupportSuggestions',
      file_count: files.length,
      market: currentMarket
    });
  };

  return (
    <>

      <PageWidth $useMaxWidth={true} $maxWidth={1024}>
        <PageContainer>

          <BackButton onClick={handleBackClick} size={Size.Small}>
            {t('Back', { ns: 'qcprSupport' })}
          </BackButton>

          <StepTitle>{t(`Have suggestions?`, { ns: 'qcprSupport' })}</StepTitle>
          <StepDescription>
            {t('Choose the option that best describes what you need help with', { ns: 'qcprSupport' })}
          </StepDescription>

          <InputLabel inputId="email" text={t('Email', { ns: 'qcprSupport' })} size={Size.Small} required={true} />
          <TextField
            id="email"
            data-testId="email"
            validationMessage={
              !!email && !/^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i.test(email)
                ? t('Invalid email address', { ns: 'qcprSupport' })
                : ''
            }
            placeholder={t('e.g. annie.laerdal@laerdal.com', { ns: 'qcprSupport' })}
            size={Size.Medium}
            value={email}
            onChange={(value) => setEmail(value)}
          />

          <InputLabel inputId="firstName" text={t('First Name', { ns: 'qcprSupport' })} size={Size.Small} required={false} />
          <TextField
            id="firstName"
            data-testId="firstName"
            placeholder={t('e.g. Annie', { ns: 'qcprSupport' })}
            size={Size.Medium}
            value={firstName}
            onChange={(value) => setFirstName(value)}
          />

          <InputLabel inputId="lastName" text={t('Last Name', { ns: 'qcprSupport' })} size={Size.Small} required={false} />
          <TextField
            id="lastName"
            data-testId="lastName"
            placeholder={t('e.g. Doe', { ns: 'qcprSupport' })}
            size={Size.Medium}
            value={lastName}
            onChange={(value) => setLastName(value)}
          />


          <InputLabel inputId="Country" text={t('Country', { ns: 'qcprSupport' })} size={Size.Small} required={true} />
          <DropdownFilter
            scrollable={true}
            id="Country"
            dataTestId='CountryId'
            placeholder={t('-- Select an option --', { ns: 'qcprSupport' })}
            onSelect={(value) => setCountry(value)}
            messageOnNoResults={t('No results found', { ns: 'qcprSupport' })}
            size={Size.Medium}
            list={countryList}
          />

          <InputLabel inputId="Description" text={t('Description', { ns: 'qcprSupport' })} size={Size.Small} required={true} />
          <CustomTextareaContainera>

            <Textarea
              id="Description"
              size={Size.Medium}
              rows={6}
              data-testId="Description"
              onChange={(value) => setDescription(value)}
              placeholder={t('Please share any suggestions or feedback. Your input helps us improve and better serve you.', { ns: 'qcprSupport' })}
            />
          </CustomTextareaContainera>

          <ChoseFileContainer
            onClick={handleClick}>

            <ComponentM textStyle={ComponentTextStyle.Bold} color={COLORS.neutral_500}>{t('Take a photo or choose files', { ns: 'qcprSupport' })}</ComponentM>

            <input
              type="file"
              ref={fileInputRef}
              onChange={handleFileInputChange}
              multiple
              style={{ display: 'none' }}
            />

            <Button icon={<SystemIcons.Upload />} onClick={handleButtonClick} variant="secondary">{t('Add a photo', { ns: 'qcprSupport' })}</Button>
          </ChoseFileContainer>

          {files.length > 0 && (
            <AttachmentsContainer>
              <AttachmentHeader>
                <ComponentXS textStyle={ComponentTextStyle.Bold}>{t('Images')} </ComponentXS>
              </AttachmentHeader>
              {fileSizeOverLimit &&
                <ComponentM color={COLORS.critical_500}>
                  {t('Files too large! Maximum is 20MB')}
                </ComponentM>
              }

              <>
                {files.map((file, index) => (
                  <AttachmentDetails key={index}>
                    <IconButton variant="secondary" action={() => handleRemoveFile(index)}>
                      <SystemIcons.Close color={COLORS.neutral_500} />
                    </IconButton>

                    <ComponentS color={COLORS.neutral_500} >{file.name}</ComponentS>
                  </AttachmentDetails>
                ))}
              </>
            </AttachmentsContainer>
          )}

          <Checkbox
            size={Size.Small}
            label={t('By checking this box, I acknowledge that I have read and understood the Laerdal Privacy Statement provided below. I consent to the storage, sharing, and processing of my personal information, including my name, phone number, and email address, which I have provided on this form, for the purposes outlined in the Laerdal Privacy Statement.')}
            selected={privacyStatement}
            select={(s) => setPrivacyStatement(s)}>
          </Checkbox>

          <LinkContainer>
            <HyperLink href='https://laerdal.com/support/customer-service/privacy-policy/' variant='default'  >
              {t('Laerdal Privacy Statement', { ns: 'qcprSupport' })}
            </HyperLink>
          </LinkContainer>

          <Button
            disabled={
              fileSizeOverLimit ||
              !privacyStatement ||
              !description ||
              !country ||
              !email ||
              !/^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i.test(email)
            }
            size={Size.Large}
            variant='primary'
            loading={creatingCase}
            onClick={() => {
              setCreatingCase(true);

              posthog.capture('SUPP-QcprSupport-SuggestionsCaseSubmitStarted', {
                page: 'QcprSupportSuggestions',
                has_email: !!email,
                has_first_name: !!firstName,
                has_last_name: !!lastName,
                has_country: !!country,
                has_description: !!description,
                files_attached: files.length,
                market: currentMarket
              });

              const caseDto: CaseCreateDto = {
                country: country,
                email: email,
                subject: "QCPR Support Suggestion",
                howCanWeHelp: 'Other',
                caseType: 'Product support',
                caseSubtype: "Improvement suggestion",
                firstName: firstName,
                lastName: lastName,
                description: description,
                option: null,
                orderOrPoNumber: null,
                serialNumber: null,
                suboption: null,
                customerNumber: null,
                productNumber: '810-09050'
              }
              caseDto.description = caseDto.description?.replace(/^\s*$(?:\r\n?|\n)/gm, '');

              CaseApi.CreateCase(caseDto, files).then((caseNumber) => {
                posthog.capture('SUPP-QcprSupport-SuggestionsCaseSubmitSuccess', {
                  page: 'QcprSupportSuggestions',
                  case_number: caseNumber,
                  market: currentMarket
                });
                setCreatingCase(false);
                navigate(buildMarketPath(currentMarket, `/qcpr-support/suggestions/created?caseNumber=${caseNumber}`));
              }).catch((error) => {
                posthog.capture('SUPP-QcprSupport-SuggestionsCaseSubmitError', {
                  page: 'QcprSupportSuggestions',
                  error: error.message || 'Unknown error',
                  market: currentMarket
                });
                setCreatingCase(false);
              });
            }}>
            {t("Create a case", { ns: 'qcprSupport' })}
          </Button>
        </PageContainer>
      </PageWidth>
    </>
  );
};

export default QcprSupportSuggestionsPage;
